"use client";

import { useEffect, useState } from "react";

import {
  getWorkspaceById,
  getWorkspaceBySubdomain,
} from "@/server-actions/workspaces/actions";

/**
 * The name and logo shown in the header when the session cannot supply them.
 *
 * Two cases: a studio subdomain (`acme.salesably.ai`, or `?subdomain=acme`) on
 * a signed-out page, and a public sharing page, where the only clue to whose
 * content is on screen is the `workspace` query param.
 */
export function useStudioBranding({ session, searchParams, normalizedPathname }) {
  const [preSubdomain, setPreSubdomain] = useState("");
  const [displayName, setDisplayName] = useState(null);
  const [displayLogo, setDisplayLogo] = useState(null);

  useEffect(() => {
    // Access window only after the component is mounted (client-side).
    setPreSubdomain(window.location.hostname.split(".salesably.ai")[0]);
  }, []);

  const subdomainParam = searchParams.get("subdomain") || preSubdomain;
  const sharedWorkspaceId = searchParams.get("workspace");

  useEffect(() => {
    if (!subdomainParam || subdomainParam === "app" || subdomainParam === "dev") {
      return;
    }

    getWorkspaceBySubdomain(subdomainParam)
      .then((result) => {
        if (result?.success && result.workspace) {
          setDisplayName(result.workspace.name);
          if (result.workspace.image) setDisplayLogo(result.workspace.image);
        }
      })
      .catch((err) => console.error("Error fetching workspace:", err));
  }, [subdomainParam]);

  useEffect(() => {
    if (session || !sharedWorkspaceId) return;
    if (!normalizedPathname.startsWith("/sharing")) return;

    getWorkspaceById(sharedWorkspaceId)
      .then((result) => {
        if (result?.success && result.workspace) {
          setDisplayName(result.workspace.name);
          if (result.workspace.image) setDisplayLogo(result.workspace.image);
        }
      })
      .catch((err) => console.error("Error fetching shared workspace:", err));
  }, [session, sharedWorkspaceId, normalizedPathname]);

  return { displayName, displayLogo };
}
